import { IconButton } from '@affine/component';
import { AffineErrorBoundary } from '@affine/core/components/affine/affine-error-boundary';
import { FetchService } from "@affine/core/modules/cloud";
import { RightSidebarIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService, WorkspaceService } from '@toeverything/infra';
import { Flex } from 'antd'
import React, { Suspense, useCallback, useEffect, useRef } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { AppSidebarService } from '../../app-sidebar';
import { SidebarSwitch } from '../../app-sidebar/views/sidebar-header';
import { ViewService } from '../services/view';
import { WorkbenchService } from '../services/workbench';
import * as styles from './route-container.css';
import { useViewPosition } from './use-view-position';
import { ViewBodyTarget, ViewHeaderTarget } from './view-islands';
import { usePageHelper } from "@affine/core/components/blocksuite/block-suite-page-list/utils";
import { useLocation } from 'react-router-dom';
import { isWorkspaceDetail } from "@affine/core/utils";

export interface Props {
  route: {
    Component: React.ComponentType;
  };
}

const ToggleButton = ({
  onToggle,
  className,
  show,
}: {
  onToggle?: () => void;
  className: string;
  show: boolean;
}) => {
  return (
    <IconButton
      size="24"
      onClick={ onToggle }
      className={ className }
      data-show={ show }
      data-testid="right-sidebar-toggle"
    >
      <svg viewBox="0 0 1024 1024" width="40" height="40">
        <path
          d="M425.459512 951.221073h-199.804878c-27.473171 0-52.44878-22.478049-52.44878-52.44878v-189.814634c-44.956098-7.492683-94.907317-24.97561-117.385366-62.439025-14.985366-24.97561-17.482927-52.44878-4.995122-79.921951 24.97561-52.44878 62.439024-92.409756 84.917073-114.887805 14.985366-77.42439 79.921951-384.62439 402.107317-384.62439 179.82439 0 324.682927 82.419512 389.619512 217.287805v2.497561c67.434146 144.858537 29.970732 322.185366-102.4 489.521951v124.878049c0 9.990244-9.990244 17.482927-19.980488 17.482926s-17.482927-9.990244-17.482926-19.980487v-129.873171c0-4.995122 2.497561-7.492683 4.995122-12.487805 127.37561-154.84878 164.839024-319.687805 102.4-452.058537v-2.49756c-57.443902-124.878049-189.814634-197.307317-357.15122-197.307318-74.926829 0-319.687805 24.97561-362.146341 362.146342 0 4.995122-2.497561 9.990244-7.492683 12.487805-17.482927 14.985366-57.443902 52.44878-82.419512 104.897561-7.492683 14.985366-7.492683 32.468293 2.49756 44.956097 14.985366 22.478049 52.44878 39.960976 107.395122 47.453659 9.990244 0 17.482927 9.990244 17.482927 17.482927v207.297561c0 7.492683 7.492683 14.985366 14.985366 14.985366h199.804878c9.990244 0 19.980488 7.492683 19.980488 19.980487s-12.487805 14.985366-22.478049 14.985366z"
          fill="#5E4EFF"></path>
        <path
          d="M343.04 731.435707l32.468293-87.414634h144.858536l32.468293 87.414634h39.960976l-124.878049-317.190244h-42.458537l-124.878049 317.190244h42.458537z m167.336585-119.882927h-122.380487l59.941463-157.346341h2.497561l59.941463 157.346341z m154.848781 119.882927v-317.190244h-34.965854v317.190244h34.965854z"
          fill="#5E4EFF"></path>
      </svg>
    </IconButton>
  );
};

export const RouteContainer = () => {
  const viewPosition = useViewPosition();
  const appSidebarService = useService(AppSidebarService).sidebar;
  const workspace = useService(WorkspaceService).workspace;
  const leftSidebarOpen = useLiveData(appSidebarService.open$);
  const workbench = useService(WorkbenchService).workbench;
  const view = useService(ViewService).view;
  const sidebarOpen = useLiveData(workbench.sidebarOpen$);
  const sidebarTabs = useLiveData(view.sidebarTabs$);
  const { createPage } = usePageHelper(workspace.docCollection);
  const location = useLocation()
  const handleToggleSidebar = useCallback(() => {
    workbench.toggleSidebar();
  }, [ workbench ]);

  const activeUser = useRef({})
  const fetchService = useService(FetchService)
  const getSession = () => {
    return fetchService.fetch('/api/auth/session', {
      headers: {
        'Content-Type': 'application/json',
      }
    }).then(res => res.json())
      .then(data => activeUser.current = data.user)
  }

  useEffect(() => {
    getSession()
  }, []);

  const params = useParams()

  useEffect(() => {
    // 侧边栏打开且没有打开文档，自动创建文档
    if (sidebarOpen && !sidebarTabs.length) {
      console.log('自动创建文档')
      createPage('page', true)
    }
  }, [ sidebarOpen ]);

  // 路由变化
  const prevPathRef = useRef("");
  useEffect(() => {
    const prevPath = prevPathRef.current;
    const currentPath = window.location.pathname;
    if (isWorkspaceDetail(prevPath) && !isWorkspaceDetail(currentPath)) {
      // 离开编辑页
      console.log('离开编辑页面，自动关闭侧边')
      if (sidebarOpen) workbench.toggleSidebar()
    }
    prevPathRef.current = currentPath;
  }, [ location ]);

  return (
    <div className={ styles.root }>
      <div className={ styles.header }>
        { !BUILD_CONFIG.isElectron && viewPosition.isFirst && (
          <SidebarSwitch
            show={ !leftSidebarOpen }
            className={ styles.leftSidebarButton }
          />
        ) }
        <ViewHeaderTarget
          viewId={ view.id }
          className={ styles.viewHeaderContainer }
        />
        { !BUILD_CONFIG.isElectron && viewPosition.isLast && (
          // 主页的头部右边的展开按钮
          <Flex align={ 'center' }>
            <ToggleButton
              show={ !sidebarOpen }
              className={ styles.rightSidebarButton }
              onToggle={ handleToggleSidebar }
            />
          </Flex>
        ) }
      </div>

      <AffineErrorBoundary>
        <Suspense>
          <Outlet />
        </Suspense>
      </AffineErrorBoundary>
      <ViewBodyTarget
        viewId={ view.id }
        className={ styles.viewBodyContainer }
        style={ {
          padding: params.pageId ? '0' : '35px 41px 20px 40px',
          overflow: 'auto'
        } }
      />
    </div>
  );
};
